import { useState, forwardRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import { IoMdAdd } from 'react-icons/io';
import { IFile, IUploadedDocument } from '../../../../models/document.model';
import { useDocumentStore, useOnboardingStore } from '../../../../store';
import GoBack from '../../../../components/base/go-back';
import BaseInfo from '../../../../components/base/info';
import CustomDropdown from '../../../../components/base/custom-dropdown';
import AppFileUpload from '../../../../components/base/app-file-upload';
import AppButton from '../../../../components/base/button';
import { FiPlus } from 'react-icons/fi';

const initDocumentType = {
  label: '',
  name: 'Select Document Type',
};

const DocumentUpload = forwardRef(() => {
  const { businessDocuments, setBusinessDocuments } = useOnboardingStore();
  const [unUploadedDocuments, setUnuploadedDocuments] = useState<
    IUploadedDocument[]
  >([]);
  const [showSelector, setShowSelector] = useState(false);
  const [currentlyUploading, setCurrentlyUploading] = useState('');
  const { removeDocument } = useDocumentStore();

  useEffect(() => {
    const unuploadedDocuments: IUploadedDocument[] = [];
    businessDocuments.forEach((item) => {
      if (item.docId) return;
      unuploadedDocuments.push(item);
    });
    setUnuploadedDocuments(unuploadedDocuments);
  }, [businessDocuments]);

  const handleFileUpload = async (uploadData: IFile) => {
    if (uploadData) {
      const nextUploadedDocuments = businessDocuments.map((doc) => {
        return doc.docLabel === currentlyUploading
          ? {
              ...doc,
              docId: uploadData.fileId ?? null,
              fileName: uploadData.fileName,
              fileType: uploadData.fileType,
              base64File: uploadData.base64File,
            }
          : doc;
      });
      setBusinessDocuments(nextUploadedDocuments);
      setCurrentlyUploading('');
      setShowSelector(false);
    }
  };

  const removeDocumentHandler = async (docId: string | null) => {
    if (!docId) {
      return toast.error('Invalid document ID.');
    }

    const response = await removeDocument(docId);
    if (response && response.isSuccess) {
      toast.success('Document removed successfully.');
      const nextUploadedDocuments = businessDocuments.map((doc) => {
        return doc.docId === docId
          ? {
              ...doc,
              docId: null,
              fileName: '',
              fileType: '',
              base64File: '',
            }
          : doc;
      });

      setBusinessDocuments(nextUploadedDocuments);
      setTimeout(() => {
        if (unUploadedDocuments.length === nextUploadedDocuments.length - 1) {
          setCurrentlyUploading('');
          setShowSelector(false);
        }
      }, 500);
    } else {
      toast.error('Could not remove document.');
    }
  };
  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <GoBack text="Go back" path="/start-kyb" />

        <h2 className="text-2xl font-bold text-[#111827]">
          Upload Your KYB Documents
        </h2>
        <p className="text-gray-text text-sm">
          Please upload your KYB documents. These are required to verify your
          business and ensure regulatory compliance.
        </p>
      </div>

      <div className="flex items-center text-sm font-bold mb-6">
        <span className="mr-4 text-gray-500">KYB Documents</span>
        <div className="flex-1 h-px bg-gray-300"></div>
      </div>
      <div className="border p-5 rounded-xl w-full flex flex-col gap-4">
        <BaseInfo type="information" text="You can upload up to 3 files" />
        {showSelector && unUploadedDocuments.length ? (
          <div>
            <CustomDropdown
              id={`kyb-document-upload`}
              label={'Document Type'}
              options={[
                initDocumentType,
                ...unUploadedDocuments.map((doc) => ({
                  label: doc.docLabel,
                  name: doc.docValue,
                })),
              ]}
              selectedValue={currentlyUploading}
              onSelectChange={(value) => {
                setCurrentlyUploading(value);
              }}
            />
          </div>
        ) : (
          <></>
        )}
        <div className="flex gap-2 flex-wrap">
          {businessDocuments.map((doc, index) =>
            doc.docId || currentlyUploading === doc.docLabel ? (
              <div
                key={index}
                className={`${currentlyUploading === doc.docLabel ? 'w-full flex-0 order-0' : 'order-1'}`}
              >
                <AppFileUpload
                  id={doc.docLabel}
                  type={'document'}
                  fileUploaded={(fileData) => handleFileUpload(fileData)}
                  onRemove={() => removeDocumentHandler(doc.docId)}
                  uploadFile={true}
                  documentType={doc.docValue}
                />
              </div>
            ) : (
              <></>
            ),
          )}
        </div>
        {unUploadedDocuments.length && !showSelector ? (
          <AppButton
            type="gray"
            label={`${businessDocuments.some((a) => a.docId !== null) ? 'Add more' : 'Add Document'}`}
            icon={<FiPlus />}
            clicked={() => setShowSelector(true)}
          />
        ) : (
          <></>
        )}
      </div>
    </div>
  );
});

// Add displayName for debugging
DocumentUpload.displayName = 'DocumentUpload';

export default DocumentUpload;
